<template>
  <div>
    <vx-card class="mb-4">
      <div class="flex flex-wrap items-center justify-between mb-4">
        <div>
          <h4 class="mb-1">{{ $t('occupancy.title') }}</h4>
          <div class="text-muted">{{ $t('occupancy.subtitle') }}</div>
        </div>
        <div class="flex items-center gap-2">
          <b-form-group class="mb-0 mr-2" :label="$t('occupancy.weeks')" label-cols="auto" label-size="sm">
            <b-form-select v-model="weeks" :options="weekOptions" size="sm" @change="load"/>
          </b-form-group>
          <b-button variant="outline-primary" size="sm" @click="load">
            <feather-icon icon="RefreshCwIcon" class="mr-50"/>
            <span class="align-middle">Atualizar</span>
          </b-button>
        </div>
      </div>

      <!-- Resumo -->
      <div class="vx-row mb-4" v-if="summary">
        <div class="vx-col sm:w-1/5 w-1/2 mb-2" v-for="card in summaryCards" :key="card.label">
          <div class="border rounded p-1 text-center">
            <div class="text-muted"><small>{{ card.label }}</small></div>
            <div class="font-bold text-lg">{{ card.value }}</div>
          </div>
        </div>
      </div>

      <div v-if="weekdays.length === 0" class="text-center text-muted p-4">
        {{ $t('occupancy.empty') }}
      </div>

      <div v-for="day in weekdays" :key="day.weekday" class="mb-3">
        <div class="font-bold border-b pb-1 mb-2 capitalize">{{ day.label }}</div>
        <div class="occupancy-grid">
          <div
            v-for="slot in day.slots"
            :key="`${slot.lessonId}-${slot.availableScheduleId}`"
            class="occupancy-cell border rounded p-1"
            :class="rateClass(slot)"
            v-tooltip.auto="{ content: tooltipFor(slot), delay: { show: 300, hide: 100 } }"
            @click="goToAgenda(slot)">
            <div class="flex items-center justify-between">
              <span class="font-medium">{{ slot.startTime }}</span>
              <span class="font-bold">{{ slot.rate === null ? '∞' : `${slot.rate}%` }}</span>
            </div>
            <div class="text-muted truncate"><small>{{ slot.lessonName }}</small></div>
            <div class="text-muted">
              <small>
                {{ slot.avgOccupied }}/{{ slot.capacity === null ? '∞' : slot.capacity }}
                <span v-if="slot.subscriptionTotal > 0"> · {{ slot.subscriptionTotal }} fixos</span>
              </small>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap gap-4 mt-3" v-if="weekdays.length">
        <span class="flex items-center gap-1"><span class="legend-dot rate-free"></span><small>{{ $t('occupancy.legend_free') }}</small></span>
        <span class="flex items-center gap-1"><span class="legend-dot rate-busy"></span><small>{{ $t('occupancy.legend_busy') }}</small></span>
        <span class="flex items-center gap-1"><span class="legend-dot rate-full"></span><small>{{ $t('occupancy.legend_full') }}</small></span>
      </div>
    </vx-card>
  </div>
</template>

<script>
import { BButton, BFormGroup, BFormSelect } from 'bootstrap-vue'
import LegalEntityService from '@/services/api/LegalEntityService'
import { mapGetters } from 'vuex'

/**
 * Mapa de ocupação semanal: onde sobra e onde falta vaga.
 *
 * A composição por origem é o que separa duas situações que o número sozinho
 * confunde — uma turma cheia de assinantes é receita previsível, uma cheia de
 * avulsos é rotatividade.
 */
export default {
  name: 'OccupancyIndex',
  components: { BButton, BFormGroup, BFormSelect },
  data: () => ({
    service: null,
    weeks: 4,
    weekOptions: [
      { value: 2, text: '2 semanas' },
      { value: 4, text: '4 semanas' },
      { value: 8, text: '8 semanas' },
    ],
    weekdays: [],
    summary: null,
  }),
  computed: {
    ...mapGetters('dataAccess', ['activeInstitutionId']),
    summaryCards() {
      if (!this.summary) return []
      return [
        { label: this.$t('occupancy.capacity'), value: this.summary.capacity },
        { label: this.$t('occupancy.occupied'), value: this.summary.occupied },
        { label: this.$t('occupancy.subscription'), value: this.summary.subscription },
        { label: this.$t('occupancy.single'), value: this.summary.single },
        { label: this.$t('occupancy.rate'), value: this.summary.rate === null ? '—' : `${this.summary.rate}%` },
      ]
    },
  },
  mounted() {
    this.service = LegalEntityService.build(this.$vs)
    this.$store.dispatch('dataAccess/setupFromLogin').finally(() => this.load())
    // A grade é por estabelecimento; trocar o clube no navbar tem de recarregar.
    this.$root.$on('activeLegalEntityChanged', this.load)
  },
  beforeDestroy() {
    this.$root.$off('activeLegalEntityChanged', this.load)
  },
  methods: {
    load() {
      const legalEntityId = this.activeInstitutionId
      if (!legalEntityId) return

      this.$vs.loading()
      this.service.getOccupancyGrid(legalEntityId, this.weeks).then(
        data => {
          this.weekdays = data.weekdays || []
          this.summary = data.summary || null
        },
        () => this.notifyError(this.$vs, this.$t('messages.error')),
      ).finally(() => this.$vs.loading.close())
    },
    rateClass(slot) {
      if (slot.rate === null) return 'rate-free'
      if (slot.rate >= 100) return 'rate-full'
      if (slot.rate >= 70) return 'rate-busy'
      return 'rate-free'
    },
    tooltipFor(slot) {
      const cap = slot.capacity === null ? 'ilimitada' : slot.capacity
      return `${slot.lessonName} · ${slot.startTime}-${slot.endTime}
Capacidade: ${cap} · Média ocupada: ${slot.avgOccupied}
Assinatura: ${slot.subscriptionTotal} · Avulso: ${slot.singleTotal}
Ocorrências: ${slot.occurrences}${slot.blocked ? ` · ${slot.blocked} bloqueada(s)` : ''}`
    },
    goToAgenda(slot) {
      this.$router.push(`/lessons/${slot.lessonId}/agenda`)
    },
  },
}
</script>

<style lang="scss" scoped>
.occupancy-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 0.5rem;
}

.occupancy-cell {
  cursor: pointer;
  transition: transform 0.1s ease;

  &:hover {
    transform: translateY(-1px);
  }
}

.rate-free {
  background: rgba(40, 199, 111, 0.12);
}

.rate-busy {
  background: rgba(255, 159, 67, 0.16);
}

.rate-full {
  background: rgba(234, 84, 85, 0.16);
}

.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 3px;
  display: inline-block;

  &.rate-free { background: rgba(40, 199, 111, 0.5); }
  &.rate-busy { background: rgba(255, 159, 67, 0.6); }
  &.rate-full { background: rgba(234, 84, 85, 0.6); }
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
